import { Component, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { HttpService } from 'src/app/services/http.service';
import { resolveApiErrorMessage } from 'src/app/utils/api-error-message.util';
import { environment } from 'src/environments/environment';

interface HubEnrollmentRow {
  enrollmentId?: string;
  hubId: string;
  hubName?: string;
  ownerName?: string;
  ownerEmail?: string;
  enabled: boolean;
  paused: boolean;
  v2Enabled: boolean;
  updatedAt?: string;
  lastAdminUpdatedBy?: string;
  lastAdminReason?: string;
}

type HubEnrollmentAction =
  | 'disable-network'
  | 'enable-network'
  | 'pause'
  | 'unpause'
  | 'enable-v2'
  | 'disable-v2';

interface PendingAction {
  row: HubEnrollmentRow;
  action: HubEnrollmentAction;
  title: string;
  description: string;
}

@Component({
  selector: 'app-delivery-hub-network',
  templateUrl: './delivery-hub-network.component.html',
  styleUrls: ['./delivery-hub-network.component.scss'],
})
export class DeliveryHubNetworkComponent implements OnInit {
  private readonly baseUrl = `${environment.apiUrl}/admin-center/delivery-network/v1`;

  data: HubEnrollmentRow[] = [];
  isLoading = false;
  featureDisabled = false;
  isSaving = false;

  search = '';
  enabledFilter = '';
  pausedFilter = '';
  v2Filter = '';

  actionDialogVisible = false;
  pendingAction: PendingAction | null = null;
  actionReason = '';

  readonly reasonExamples = [
    'Unpaid network fees',
    'Operational pause',
    'Temporary capacity limit',
    'Re-enabled after payment',
  ];

  constructor(
    private httpService: HttpService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    const params = new URLSearchParams();
    if (this.enabledFilter !== '') {
      params.set('enabled', this.enabledFilter);
    }
    if (this.pausedFilter !== '') {
      params.set('paused', this.pausedFilter);
    }
    if (this.v2Filter !== '') {
      params.set('v2Enabled', this.v2Filter);
    }
    if (this.search.trim()) {
      params.set('search', this.search.trim());
    }
    params.set('page', '0');
    params.set('size', '200');

    const query = params.toString() ? `?${params.toString()}` : '';
    try {
      const response = await this.httpService
        .doGet(`${this.baseUrl}/hubs/enrollments${query}`)
        .toPromise();
      this.data = Array.isArray(response) ? response : [];
      this.featureDisabled = false;
    } catch (error: any) {
      this.data = [];
      if (error?.status === 404) {
        this.featureDisabled = true;
      } else {
        this.toastError(resolveApiErrorMessage(error, 'Could not load hub enrollments'));
      }
    } finally {
      this.isLoading = false;
    }
  }

  openAction(row: HubEnrollmentRow, action: HubEnrollmentAction): void {
    this.pendingAction = {
      row,
      action,
      title: this.actionTitle(action),
      description: this.actionDescription(action, row),
    };
    this.actionReason = '';
    this.actionDialogVisible = true;
  }

  async confirmAction(): Promise<void> {
    if (!this.pendingAction) {
      return;
    }
    const reason = this.actionReason.trim();
    if (!reason) {
      this.toastError('A reason is required for hub network access changes');
      return;
    }

    const body = this.buildPatchBody(this.pendingAction.action);
    if (!body) {
      return;
    }
    body.reason = reason;

    this.isSaving = true;
    try {
      await this.httpService
        .doPatch(
          `${this.baseUrl}/hubs/${encodeURIComponent(this.pendingAction.row.hubId)}/enrollment`,
          body,
        )
        .toPromise();
      this.toastSuccess(`${this.pendingAction.title} applied`);
      this.actionDialogVisible = false;
      this.pendingAction = null;
      this.loadData();
    } catch (error: any) {
      this.toastError(resolveApiErrorMessage(error, 'Could not update hub enrollment'));
    } finally {
      this.isSaving = false;
    }
  }

  copyHubId(hubId: string): void {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(hubId).then(
        () => this.toastSuccess('Hub ID copied'),
        () => this.toastError('Could not copy Hub ID'),
      );
    }
  }

  formatDate(value?: string): string {
    if (!value) {
      return '—';
    }
    const d = new Date(value);
    return Number.isFinite(d.getTime()) ? d.toLocaleString() : value;
  }

  networkStatusClass(row: HubEnrollmentRow): string {
    if (!row.enabled) {
      return 'chip-disabled';
    }
    if (row.paused) {
      return 'chip-paused';
    }
    return 'chip-enabled';
  }

  networkStatusLabel(row: HubEnrollmentRow): string {
    if (!row.enabled) {
      return 'Disabled';
    }
    if (row.paused) {
      return 'Paused';
    }
    return 'Enabled';
  }

  v2StatusClass(row: HubEnrollmentRow): string {
    return row.v2Enabled ? 'chip-v2-on' : 'chip-v2-off';
  }

  private buildPatchBody(action: HubEnrollmentAction): Record<string, boolean | string> | null {
    switch (action) {
      case 'disable-network':
        return { enabled: false };
      case 'enable-network':
        return { enabled: true };
      case 'pause':
        return { paused: true };
      case 'unpause':
        return { paused: false };
      case 'enable-v2':
        return { v2Enabled: true };
      case 'disable-v2':
        return { v2Enabled: false };
      default:
        return null;
    }
  }

  private actionTitle(action: HubEnrollmentAction): string {
    switch (action) {
      case 'disable-network':
        return 'Disable network';
      case 'enable-network':
        return 'Enable network';
      case 'pause':
        return 'Pause hub';
      case 'unpause':
        return 'Unpause hub';
      case 'enable-v2':
        return 'Enable V2 broadcast';
      case 'disable-v2':
        return 'Disable V2 broadcast';
      default:
        return 'Update hub';
    }
  }

  private actionDescription(action: HubEnrollmentAction, row: HubEnrollmentRow): string {
    const hub = row.hubName || row.hubId;
    switch (action) {
      case 'disable-network':
        return `Disable delivery network access for ${hub}. Broadcast and network assignment will be blocked.`;
      case 'enable-network':
        return `Re-enable delivery network access for ${hub}.`;
      case 'pause':
        return `Pause delivery network dispatch for ${hub}. Broadcast and assignment will be blocked until unpaused.`;
      case 'unpause':
        return `Remove operational pause for ${hub}.`;
      case 'enable-v2':
        return `Enable V2 broadcast eligibility for ${hub}. Hub must already be network-enabled.`;
      case 'disable-v2':
        return `Disable V2 broadcast for ${hub}. Owner V2 dispatch panel may become unavailable; legacy paired behavior may still apply.`;
      default:
        return '';
    }
  }

  private toastSuccess(detail: string): void {
    this.messageService.add({ severity: 'success', summary: 'Successful', detail, life: 3000 });
  }

  private toastError(detail: string): void {
    this.messageService.add({ severity: 'error', summary: 'Error', detail, life: 5000 });
  }
}
